'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const [form, setForm] = useState<ContactForm>({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const sub = encodeURIComponent(form.subject || `查詢 - ${form.name}`);
    const body = encodeURIComponent(`姓名: ${form.name}\n電郵: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:hhess2071@gmail.com?subject=${sub}&body=${body}`;
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '0.75rem',
    border: '1px solid rgba(201,168,76,0.3)',
    backgroundColor: '#FDFAF5',
    color: '#2C2416',
    fontSize: '0.9rem',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    color: '#5C4A1E',
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: '0.5rem',
  };

  return (
    <div style={{ backgroundColor: '#F5F0E8', minHeight: '100vh' }}>
      <Header />

      {/* Page Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #2C2416 0%, #5C4A1E 60%, #8B6914 100%)',
          padding: '5rem 0 4rem',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p style={{ color: '#C9A84C', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.15em' }} className="mb-3">
            CONTACT US · 聯繫我們
          </p>
          <h1 style={{ color: '#F5F0E8', fontWeight: 800 }} className="text-4xl sm:text-5xl mb-4">
            聯繫我們
          </h1>
          <p style={{ color: '#A09070', maxWidth: '600px', lineHeight: 1.8 }}>
            歡迎隨時與我們聯繫，我們的團隊將竭誠為您服務。
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-5">
              <div>
                <p style={{ color: '#C9A84C', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.15em' }} className="mb-3">
                  CONTACT INFO · 聯繫方式
                </p>
                <h2 style={{ color: '#2C2416', fontWeight: 800 }} className="text-2xl sm:text-3xl mb-6">
                  與我們取得聯繫
                </h2>
              </div>

              {[
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  label: '電郵 Email',
                  value: 'hhess2071@gmail.com',
                  href: 'mailto:hhess2071@gmail.com',
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  label: '地址 Address',
                  value: '新界荃灣柴灣角街 66-82 號金熊工業中心\n66-82 Chai Wan Kok Street, Tsuen Wan, N.T.',
                  href: null,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-2xl"
                  style={{ backgroundColor: '#FDFAF5', border: '1px solid rgba(201,168,76,0.2)' }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ color: '#8B6914', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                      {item.label}
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        style={{ color: '#2C2416', fontWeight: 600, fontSize: '0.95rem' }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p style={{ color: '#2C2416', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'pre-line' }}>
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Business Hours */}
              <div
                className="p-5 rounded-2xl"
                style={{ backgroundColor: '#2C2416' }}
              >
                <h3 style={{ color: '#C9A84C', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  辦公時間 Business Hours
                </h3>
                <div className="space-y-2">
                  {[
                    { day: '週一至週五 Mon–Fri', time: '09:00 – 18:00' },
                    { day: '週六 Saturday', time: '09:00 – 13:00' },
                    { day: '週日及公眾假期', time: '休息 Closed' },
                  ].map((h, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span style={{ color: '#A09070' }}>{h.day}</span>
                      <span style={{ color: '#E8D5A3', fontWeight: 600 }}>{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div
                className="rounded-2xl p-8"
                style={{ backgroundColor: '#FDFAF5', boxShadow: '0 8px 40px rgba(44,36,22,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
              >
                <h2 style={{ color: '#2C2416', fontWeight: 800 }} className="text-2xl mb-2">
                  發送訊息
                </h2>
                <p style={{ color: '#8B6914', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Send Us a Message</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label style={labelStyle}>姓名 Name <span style={{ color: '#C9A84C' }}>*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>電郵 Email <span style={{ color: '#C9A84C' }}>*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>主題 Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Message Subject"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>訊息內容 Message <span style={{ color: '#C9A84C' }}>*</span></label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="請輸入您的訊息..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-700 text-base transition-all duration-300 hover:scale-[1.02]"
                    style={{ backgroundColor: '#C9A84C', color: '#2C2416', fontWeight: 700 }}
                  >
                    發送訊息 Send Message →
                  </button>

                  <p style={{ color: '#8B6914', fontSize: '0.78rem', textAlign: 'center' }}>
                    或直接聯繫：hhess2071@gmail.com
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
